import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import {
  HttpRequest,
  HttpInterceptor,
  HttpHandler,
  HttpEvent,
  HttpEventType,
  HttpResponse,
  HttpErrorResponse,
} from '@angular/common/http';
import { EMPTY, from, Observable } from 'rxjs';
import { Injectable, inject } from '@angular/core';
import { settings } from '../../../settings';
import { LoggingService } from 'src/app/modules/shared/services/logging/logging.service';
import { RegobsAuthService, TOKEN_RESPONSE_FULL_KEY } from 'src/app/modules/auth/services/regobs-auth.service';
import { StorageBackend } from '@openid/appauth';
import { ApiVersionService } from '../services/api-version/api-version.service';
import { Capacitor } from '@capacitor/core';

const DEBUG_TAG = 'ApiInterceptor';

/**
 * Sender innloggings-token med kall til Regobs API der kallene krever at man er logget inn.
 * Hvis api-kallet feiler pga. innloggingsfeil (HTTP 401), prøver vi å fornye tokenet og kjører kallet en gang til.
 * Sjekker også om vi får sunset-header i repons fra API'et for å varsle om at vi bruker et utdatert API.
 */
@Injectable()
export class ApiInterceptor implements HttpInterceptor {
  private regobsAuthService = inject(RegobsAuthService);
  private loggerService = inject(LoggingService);
  private storage = inject(StorageBackend);
  private apiVersionService = inject(ApiVersionService);

  //return true if given url belongs to any of the protected Regobs API urls in any environment
  private isRegObsApiThatRequireLogin(url: string): boolean {
    const apiUrls = settings.services.regObs.apiUrl;
    for (const environment of Object.keys(apiUrls)) {
      const server = apiUrls[environment];
      for (const service of ['Search/MyRegistrations', 'Registration', 'Account', 'Trip']) {
        if (url.startsWith(`${server}/${service}`)) {
          return true;
        }
      }
    }
    return false;
  }

  private isB2cApi(url: string): boolean {
    return url.indexOf('/token') > -1;
  }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (this.isB2cApi(req.url)) {
      return next.handle(req).pipe(
        tap((response) => {
          if (response.type === HttpEventType.Response) {
            this.storage.setItem(TOKEN_RESPONSE_FULL_KEY, JSON.stringify(response.body));
          }
        })
      );
    }
    if (this.isRegObsApiThatRequireLogin(req.url) && !req.headers.has('Authorization')) {
      return this.addAuthHeader(req).pipe(
        switchMap((requestWithAuthHeader) => {
          return next.handle(requestWithAuthHeader).pipe(
            catchError((err) => {
              return this.handleResponseError(err, requestWithAuthHeader, next);
            })
          );
        })
      );
    }
    return next.handle(req).pipe(
      tap((httpEvent) => {
        if (httpEvent.type === 0) {
          return; // Skip request
        }
        if (Capacitor.isNativePlatform() && httpEvent instanceof HttpResponse) {
          if (httpEvent.headers.has('sunset')) {
            const sunsetDate = httpEvent.headers.get('sunset');
            this.apiVersionService.setSunsetDate(sunsetDate as string);
          }
        }
      })
    );
  }

  private addAuthHeader(request: HttpRequest<unknown>): Observable<HttpRequest<unknown>> {
    return this.regobsAuthService.loggedInUser$.pipe(
      // We do not want this do be a long lived observable,
      // we want it to complete after we get the first loggedInUser.
      // take(1) makes the observable complete after getting the first loggedInUser.
      take(1),
      catchError((err) => {
        this.loggerService.debug('Could not get valid token', DEBUG_TAG, { err });
        this.regobsAuthService.signIn();
        // .signIn trigger en login-flyt med navigering til b2c-login.
        // EMPTY sørger for at denne requesten avbrytes uten å emitte noe videre.
        return EMPTY;
      }),
      map((user) => {
        const headers = request.headers.set('Authorization', `Bearer ${user.token}`);
        return request.clone({ headers });
      })
    );
  }

  private lastRefreshTime: number | undefined;

  private hasRefreshedRecently() {
    if (!this.lastRefreshTime) {
      return false;
    }
    const timeSinceLastRefresh = Date.now() - this.lastRefreshTime;
    const recentRefreshTime = 30_000; // 30 sekunder
    return timeSinceLastRefresh < recentRefreshTime;
  }

  private handleResponseError(
    error: unknown,
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (error instanceof HttpErrorResponse && error.status === 401) {
      // Dette kallet kan returnere 401 uansett om bruker er logget inn.
      // Kun observatører har tilgang.
      if (request.url.includes('/Trip/ObserverTrips')) {
        throw error;
      }

      if (this.hasRefreshedRecently()) {
        this.loggerService.debug('Skipping token refresh, already refreshed recently', DEBUG_TAG, {
          url: request.url,
        });
        throw error;
      }

      // Vi er ikke autorisert, trolig fordi tokenet ikke er gyldig
      this.loggerService.debug('Got 401 from API, trying to refresh token and repeat API-call...', DEBUG_TAG, {
        url: request.url,
        method: request.method,
      });
      return from(this.regobsAuthService.refreshToken()).pipe(
        tap(() => {
          this.lastRefreshTime = Date.now();
          this.loggerService.debug('Token refreshed', DEBUG_TAG, {
            url: request.url,
            method: request.method,
          });
        }),
        catchError((err) => {
          this.loggerService.debug('Token refresh failed', DEBUG_TAG, { err, url: request.url });
          throw error; // Rethrow original 401 error
        }),
        switchMap(() => this.addAuthHeader(request)),
        switchMap((req) => next.handle(req))
      );
    }
    throw error;
  }
}
